import { isEqual } from 'lodash-es'

import { BookingDTOCargoFCL, ContainerItemDTO } from '@/clients/booking'

import { FormBookingDTOCargoFCL, FormContainerItemDTO, SubmitContainerItemDTO } from './types'

/**
 * Values a container row can carry either once for the whole row, or once per
 * container unit. The array form is what the per-container checkboxes toggle.
 */
const PER_CONTAINER_FIELDS = [
  // weights keep the array form on a single unit row, which is what the always visible
  // weights checkbox reads; the address inputs only exist above quantity 1
  { array: 'grossWeights', row: 'grossWeightInKg', collapsesOnSingleUnit: false },
  { array: 'pickUpLocationIDs', row: 'pickUpLocationID', collapsesOnSingleUnit: true },
  { array: 'deliveryLocationIDs', row: 'deliveryLocationID', collapsesOnSingleUnit: true },
] as const

type PerContainerValue = number | string | null
type RowRecord = Record<string, unknown>

// the dimensions are addressed by key, which the typed row shapes do not expose,
// so reads and writes go through an untyped view of the same object
const asRecord = (row: SubmitContainerItemDTO | FormContainerItemDTO): RowRecord => row as unknown as RowRecord
const asForm = (row: RowRecord): FormContainerItemDTO => row as unknown as FormContainerItemDTO
const asSubmit = (row: RowRecord): SubmitContainerItemDTO => row as unknown as SubmitContainerItemDTO

const readArray = (row: RowRecord, key: string): PerContainerValue[] | undefined => {
  const value = row[key]
  return Array.isArray(value) ? (value as PerContainerValue[]) : undefined
}

const nulls = (length: number): null[] => Array.from({ length }, () => null)

const isFragment = (row: FormContainerItemDTO): boolean =>
  PER_CONTAINER_FIELDS.some(({ array }) => readArray(asRecord(row), array) != null)

const isValidQuantity = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0

export const normalizeQuantity = (value: unknown): number => (isValidQuantity(value) ? Math.floor(value) : 0)

const canMergeFragments = (last: FormContainerItemDTO, next: FormContainerItemDTO): boolean =>
  isFragment(last) &&
  isFragment(next) &&
  last.type === next.type &&
  last.containerNumber === next.containerNumber &&
  isEqual(last.tags, next.tags)

export const hasContainerGrossWeights = (containers: FormContainerItemDTO[] | undefined): boolean =>
  (containers ?? []).some(
    (c) => c.grossWeightInKg != null || (Array.isArray(c.grossWeights) && c.grossWeights.some((w) => w != null))
  )

/**
 * Maps BE-shaped containers into form rows. A single weighted container
 * (quantity 1) becomes a per-container fragment so adjacent same-shape
 * fragments (produced by the submit split) collapse back into one row with a
 * `grossWeights` array. A quantity>1 row with a single weight stays a single
 * inline row total. Adjacency matters so we never re-order what the BE sent.
 */
/**
 * A one unit row has no per container inputs to render, so its value has to sit on the row field
 * or it would be held in an array the form never shows and the selector would look empty.
 */
const collapseSingleUnitRow = (container: FormContainerItemDTO): FormContainerItemDTO => {
  if (normalizeQuantity(container.quantity) > 1) {
    return container
  }

  const row = asRecord({ ...container })
  PER_CONTAINER_FIELDS.forEach(({ array, row: rowKey, collapsesOnSingleUnit }) => {
    const values = readArray(row, array)
    if (!collapsesOnSingleUnit || values == null) {
      return
    }

    delete row[array]
    if (values[0] != null) {
      row[rowKey] = values[0]
    }
  })

  return asForm(row)
}

export const consolidateContainersForForm = (
  containers: SubmitContainerItemDTO[] | undefined
): FormContainerItemDTO[] => {
  if (containers == null || containers.length === 0) return []

  const merged: FormContainerItemDTO[] = []
  for (const entry of containers) {
    const quantity = normalizeQuantity(entry.quantity)
    const source = asRecord(entry)
    const row: RowRecord = { ...entry, quantity }

    PER_CONTAINER_FIELDS.forEach(({ array, row: rowKey }) => {
      const value = source[rowKey]
      delete row[rowKey]

      if (value == null) {
        return
      }

      // a single unit line is a per-container fragment so split lines merge back
      if (quantity === 1) {
        row[array] = [value]
      } else {
        row[rowKey] = value
      }
    })

    const fragment = asForm(row)
    const last = merged[merged.length - 1]

    if (last != null && canMergeFragments(last, fragment)) {
      const lastQuantity = normalizeQuantity(last.quantity)
      const lastRecord = asRecord(last)
      lastRecord.quantity = lastQuantity + quantity

      PER_CONTAINER_FIELDS.forEach(({ array }) => {
        const lastValues = readArray(lastRecord, array)
        const nextValues = readArray(asRecord(fragment), array)

        if (lastValues == null && nextValues == null) {
          return
        }

        // pad the side that is not per-container so every array matches quantity
        lastRecord[array] = [...(lastValues ?? nulls(lastQuantity)), ...(nextValues ?? nulls(quantity))]
      })
      continue
    }

    merged.push(fragment)
  }

  return merged.map(collapseSingleUnitRow)
}

/**
 * Flattens form rows back into BE DTO entries, per row. A per-container row
 * (carrying a `grossWeights` array) splits into `quantity` single-unit entries
 * with their own `grossWeightInKg`; a fully-empty one stays a single weightless
 * row so the container count is preserved. A single inline row keeps its
 * `grossWeightInKg` as the row total. Weightless rows pass through untouched.
 */
export const flattenContainersForSubmit = (
  containers: FormContainerItemDTO[] | undefined
): SubmitContainerItemDTO[] => {
  if (containers == null || containers.length === 0) return []

  const flattened: SubmitContainerItemDTO[] = []

  for (const row of containers) {
    const quantity = normalizeQuantity(row.quantity)
    const source = asRecord(row)

    const base: RowRecord = { ...row }
    PER_CONTAINER_FIELDS.forEach(({ array, row: rowKey }) => {
      delete base[array]
      if (base[rowKey] == null) {
        delete base[rowKey]
      }
    })

    const perContainer = PER_CONTAINER_FIELDS.map(({ array, row: rowKey }) => ({
      row: rowKey as string,
      values: readArray(source, array)?.slice(0, quantity),
    })).filter((field): field is { row: string; values: PerContainerValue[] } => field.values != null)

    // an all-empty per-container row stays one row, as if the box were never ticked
    const hasPerContainerValue = perContainer.some((field) => field.values.some((value) => value != null))

    if (!hasPerContainerValue) {
      flattened.push(asSubmit(base))
      continue
    }

    for (let unit = 0; unit < quantity; unit++) {
      const line: RowRecord = { ...base, quantity: 1 }
      perContainer.forEach(({ row: rowKey, values }) => {
        line[rowKey] = values[unit] ?? null
      })
      flattened.push(asSubmit(line))
    }
  }

  return flattened
}

export const flattenCargoFCLForSubmit = (cargoFCL: FormBookingDTOCargoFCL): BookingDTOCargoFCL => ({
  ...cargoFCL,
  containers: flattenContainersForSubmit(cargoFCL.containers) as ContainerItemDTO[],
})

/**
 * booking-v2 rejects a cargo where some containers carry a gross weight and others do not
 * (ValidateGrossWeightExclusivity). The check runs on the flattened rows, so it runs here on
 * the same output the form is about to submit rather than on the form's own shape.
 */
export const hasPartialContainerWeights = (containers: FormContainerItemDTO[] = []): boolean => {
  const submitted = flattenContainersForSubmit(containers)
  const weighted = submitted.filter((container) => container.grossWeightInKg != null && container.grossWeightInKg > 0)

  return weighted.length > 0 && weighted.length !== submitted.length
}
